import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { getProductAvailability, updateProductAvailability } from '../../../shared/api/availability.js'
import type { Product } from '../../../shared/types/models.js'
import { ApiError } from '../../../shared/types/api.js'

interface Props {
  organizationId: string
  product: Product | null
  disabled: boolean
}

export default function AvailabilitySection({ organizationId, product, disabled }: Props) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saveError, setSaveError] = useState('')
  const [toggles, setToggles] = useState<Record<string, boolean>>({})
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)

  // Product does not exist yet (create mode): availability can be managed after creation
  if (!product) {
    return (
      <div className="settings-card">
        <h4>{t('products.availability')}</h4>
        <p className="page-section-description">{t('products.variantsCreateNote')}</p>
      </div>
    )
  }

  const pid = product.id
  const archived = product.status === 'ARCHIVED'

  async function load() {
    setLoading(true)
    setError('')
    try {
      const avail = await getProductAvailability(organizationId, pid)
      const map: Record<string, boolean> = {}
      if (avail) {
        for (const item of avail) {
          map[item.locationId] = item.isAvailable
        }
      }
      setToggles(map)
      setDirty(false)
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : t('products.availabilityLoadError'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [organizationId, pid])

  async function handleToggle(locationId: string, value: boolean) {
    if (disabled || saving) return
    setToggles(prev => ({ ...prev, [locationId]: value }))
    setDirty(true)
    setSaveError('')
  }

  async function handleSave() {
    if (!dirty || saving) return
    setSaving(true)
    setSaveError('')
    try {
      const items = Object.entries(toggles).map(([locationId, isAvailable]) => ({ locationId, isAvailable }))
      const result = await updateProductAvailability(organizationId, pid, { availability: items })
      const map: Record<string, boolean> = {}
      for (const item of result) {
        map[item.locationId] = item.isAvailable
      }
      setToggles(map)
      setDirty(false)
    } catch (err: unknown) {
      setSaveError(err instanceof ApiError ? err.message : t('products.availabilitySaveError'))
    } finally {
      setSaving(false)
    }
  }

  const readOnly = disabled || archived

  return (
    <div className="settings-card">
      <div className="availability-header">
        <div>
          <h4>{t('products.availability')}</h4>
          <p className="page-section-description">{t('products.availability')}</p>
        </div>
      </div>

      {loading && <div className="page-loading"><div className="spinner" /></div>}

      {error && <div className="form-error-banner">{error}</div>}
      {saveError && <div className="form-error-banner">{saveError}</div>}

      {!loading && !error && Object.keys(toggles).length === 0 && (
        <p className="page-section-description">{t('products.availabilityNoLocations')}</p>
      )}

      {!loading && !error && Object.keys(toggles).length > 0 && (
        <div className="availability-list">
          {Object.entries(toggles).map(([locationId, isAvailable]) => (
            <label key={locationId} className="availability-row">
              <input
                type="checkbox"
                checked={isAvailable}
                disabled={readOnly || saving}
                onChange={e => void handleToggle(locationId, e.target.checked)}
              />
              <span className={isAvailable ? 'availability-on' : 'availability-off'}>
                {isAvailable ? t('products.available') : t('products.unavailable')}
              </span>
            </label>
          ))}
        </div>
      )}

      {dirty && !readOnly && (
        <div className="form-actions">
          <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void handleSave()}>
            {saving ? t('products.saving') : t('products.save')}
          </button>
        </div>
      )}
    </div>
  )
}